import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowsInSimple,
  ArrowsOutSimple,
  CaretDown,
  DotsThree,
  Headphones,
  Pause,
  Play,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { BrandMark } from "../brand/brand-mark";
import { cn } from "../lib/cn";
import { CARD_VARIANT_CLASS, CARD_VARIANT_STYLE } from "../primitives/card";
import { EyebrowBullet } from "../primitives/eyebrow-bullet";

/**
 * BriefSurface, "The Brief".
 *
 * Hero card on the HealthOS ambient surface. A narrative, audio-first
 * companion briefing with a named host, a rich body with teal-highlighted
 * phrases, up to three factual hero sections, a first-party audio player,
 * a sources list, and transcript / share / save actions.
 *
 * Layout follows the edge-web sectioning pattern: each logical block
 * (header / body / facts / audio / footer) is its own padded region with
 * a 1px teal-tinted divider above, so the card reads as a composed stack
 * of sections rather than a single padded box.
 *
 * Initial state clamps the narrative body to three lines and keeps the
 * factual sections (On Your Plan Today / What To Expect / One Thing To
 * Win) collapsed behind a "Show more" toggle. Expanding reveals the rest
 * of the body AND the three-column facts strip together.
 *
 * The component is display-only. All copy, host, body segments, sections,
 * sources, and `audioUrl` come from the server's ambient payload.
 */

export interface BriefHost {
  name: string;
  role?: string;
  organization?: string;
  tagline?: string;
  /** Optional absolute or app-relative URL. Falls back to initials puck. */
  photoUrl?: string;
}

export interface BriefSection {
  overline: string;
  title: string;
  body: string;
}

export type BriefBodySegment = { text: string; highlight?: boolean; emphasis?: boolean };

export interface BriefCopy {
  eyebrow?: string;
  date?: string;
  headline: string;
  subtitle?: string;
  /** Short duration label shown top-right, e.g. "4 min". */
  durationLabel?: string;
  /** Episode label shown top-right under the duration, e.g. "Episode 142". */
  episodeLabel?: string;
  host?: BriefHost;
  /**
   * Body can be plain text, a paragraph, or an array of segments so the
   * server can mark important phrases for visual emphasis.
   */
  body?: string | BriefBodySegment[];
  sections?: BriefSection[];
  sources?: string[];
  audioUrl?: string;
  updatedLabel?: string;
}

export interface BriefSurfaceProps {
  copy: BriefCopy;
  className?: string;
  /**
   * Density + palette preset.
   *
   * `full` (default) is the patient-facing hero card on AmbientPage:
   * teal-tinted radial gradient, 112px host puck, generous padding,
   * 3-line body clamp.
   *
   * `compact` is the operations-dashboard preset: ops palette
   * (white-to-leaf-soft gradient, navy headline, leaf-dark eyebrow and
   * highlights, mist-dark borders, gray body) so it sits next to the
   * Hero stat card without looking alien. The host puck is dropped, the
   * subtitle is suppressed (the headline already carries the meaning),
   * and the inner body region flex-grows to fill the grid row height so
   * the card matches the Hero's height pixel-for-pixel. "Show more"
   * does the heavy lifting for the rest of the narrative + facts strip.
   *
   * `preview` is the compact Today-rail treatment. It fills its paired rail
   * height, with playback anchored as a glanceable footer. Its host promotes
   * it to `full` when playback begins.
   */
  variant?: "full" | "compact" | "preview";
  /** When set, the "..." affordance becomes a button that calls this. */
  onMore?: () => void;
  onMoreLabel?: string;
  /** When set, a header toggle lets the brief claim (or release) a wider
   *  slot. The parent owns the layout; this only renders the affordance. */
  expand?: { expanded: boolean; onToggle: () => void; label: string };
  /** Fires whenever playback starts or stops, so the page can coordinate
   *  audio focus (duck the ambience, pause competing players). */
  onPlayingChange?: (playing: boolean) => void;
  /** Fires once when the audio reaches the end, native or Web Audio. */
  onEnded?: () => void;
  /** Increment to force-pause playback (another player took focus). */
  pauseToken?: number;
  /** Resolves an authenticated playable URL only when playback starts. */
  resolveAudioUrl?: (audioUrl: string) => Promise<string>;
}

/*
 * Web Speech API browser TTS is a fallback that was actively misleading:
 * a brand brief that should sound like a Fish Audio voice was instead
 * being narrated by the OS robot. Disabled by default; the brief now
 * shows the body as text-only when there's no real Fish Audio audio,
 * and the play button only appears once `audioUrl` is set.
 *
 * Flip this to `true` to re-enable the fallback for accessibility
 * spikes / e2e tests where browser TTS is preferable to no audio.
 */
const SUPPORTS_TTS = false;

const PLAY_BTN_SHADOW =
  "0 6px 18px -8px color-mix(in srgb, var(--color-teal-600) 75%, transparent)";

/**
 * Shared horizontal divider between sections. The teal-at-10% tint carries
 * over the hero tint without fighting the gradient background.
 */
const SECTION_DIVIDER = "border-t border-teal-900/10";

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Render a duration as a human-friendly chip label.
 * Sub-minute floors to the nearest 10s; otherwise floors to whole minutes.
 */
function formatDurationLabel(seconds: number): string | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (seconds < 60) {
    const rounded = Math.max(10, Math.floor(seconds / 10) * 10);
    return `${rounded} secs`;
  }
  const mins = Math.floor(seconds / 60);
  return `${mins} min${mins === 1 ? "" : "s"}`;
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function bodyToPlainText(body: BriefCopy["body"]): string {
  if (!body) return "";
  if (typeof body === "string") return body;
  return body.map((s) => s.text).join(" ");
}

/**
 * Podcast-style skip control. Phosphor `ArrowCounterClockwise`/`ArrowClockwise`
 * are the canonical curved-arrow glyph used by Apple Podcasts / Overcast /
 * Spotify. The "15" overlays the icon so the affordance is legible without
 * a hover tooltip.
 */
const TRANSPORT_BTN_SIZE_FULL = "h-[52px] w-[52px]";
const TRANSPORT_BTN_SIZE_COMPACT = "h-[40px] w-[40px]";
const TRANSPORT_BTN_SIZE_PREVIEW = "h-12 w-12";
const SKIP_ICON_SIZE_FULL = 44;
const SKIP_ICON_SIZE_COMPACT = 32;

function SkipChip({
  seconds,
  onClick,
  compact,
}: {
  seconds: number;
  onClick: () => void;
  compact?: boolean;
}) {
  const mag = Math.abs(seconds);
  const forward = seconds > 0;
  const Icon = forward ? ArrowClockwise : ArrowCounterClockwise;
  const sizeClass = compact ? TRANSPORT_BTN_SIZE_COMPACT : TRANSPORT_BTN_SIZE_FULL;
  const iconSize = compact ? SKIP_ICON_SIZE_COMPACT : SKIP_ICON_SIZE_FULL;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={forward ? `Skip forward ${mag} seconds` : `Skip back ${mag} seconds`}
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center rounded-full transition-colors",
        sizeClass,
        compact
          ? "text-ink-primary/85 hover:bg-navy/5 hover:text-ink-primary"
          : "text-teal-900/85 hover:bg-teal-900/5 hover:text-teal-900",
      )}
    >
      <Icon size={iconSize} weight="light" />
      {/*
       * Optical-centre offset: the curved arrow opens at the top, so visual
       * mass sits slightly below geometric centre. Nudging the digits ~1px
       * down anchors them inside the arc opening.
       */}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center pt-[1px] font-medium leading-none tabular-nums tracking-tight",
          compact ? "text-2xs" : "text-xs",
        )}
      >
        {mag}
      </span>
    </button>
  );
}

/**
 * Stylised waveform progress bar. Deterministic (seeded off index) so the
 * same brief renders identically across mounts. 72 bars, sine envelope +
 * multi-frequency jitter for a natural organic shape.
 *
 * Layout discipline (this had a stubborn invisibility bug - write it
 * down so it doesn't regress):
 *  - Grid `1fr` resolves to `minmax(auto, 1fr)`. `auto` is the row's
 *    min-content size, which for 72 flex children is effectively zero
 *    on initial render. The waveform was collapsing to 0×0. Fix: parent
 *    grid track must be `minmax(0, 1fr)` (set on the audio section in
 *    BriefSurface), and we set `min-w-0` on this row.
 *  - Bars use fixed `w-[3px]` (not `flex-1`). flex-1 compounds the
 *    min-content collapse and rounds bars to sub-pixel widths that
 *    disappear at certain container widths.
 *  - Each bar has a fixed `min-h-[2px]` floor (Wavesurfer.js issue
 *    #1693 - even silent samples should be visible).
 *  - Heights are pixel-precise (clamped to the row height) instead of
 *    percentages, so a parent without an intrinsic height can't zero
 *    them out.
 *
 * Played vs unplayed: same teal hue at 100% vs 30% opacity, per Wistia /
 * SoundCloud / Substack pattern. The colour boundary IS the playhead -
 * a separate vertical line is a Wavesurfer convention from
 * full-resolution waveforms and reads as noise here.
 */
const WAVEFORM_HEIGHT_FULL = 44;
const WAVEFORM_HEIGHT_COMPACT = 32;
const WAVEFORM_HEIGHT_PREVIEW = 32;
const BAR_MIN_H = 6;
const BAR_MAX_H = 40;
const WAVEFORM_BAR_COUNT = 72;

/**
 * Pre-computed bar heights. Deterministic (seeded off index) and constant
 * across mounts, so we generate once at module scope rather than per render.
 * Pixel-precise heights survive parents that fail to forward intrinsic height.
 */
/**
 * Precomputed bar geometry for the waveform. Each entry carries a stable
 * `id` (used as the React key index-based keys would trip biome's
 * noArrayIndexKey rule, and more importantly would lose positional
 * identity if the bar count ever became dynamic) and the `height` derived
 * from the seeded envelope + jitter.
 */
const WAVEFORM_BARS: ReadonlyArray<{ id: string; height: number }> = Array.from(
  { length: WAVEFORM_BAR_COUNT },
  (_, i) => {
    const envelope = Math.sin((i / WAVEFORM_BAR_COUNT) * Math.PI) * 0.75 + 0.25;
    const jitter = Math.sin(i * 0.7) * 0.18 + Math.sin(i * 0.23) * 0.14 + Math.cos(i * 0.11) * 0.1;
    const norm = Math.min(1, Math.max(0.18, envelope + jitter));
    return {
      id: `waveform-bar-${i}`,
      height: Math.round(BAR_MIN_H + norm * (BAR_MAX_H - BAR_MIN_H)),
    };
  },
);

/*
 * Per-bar breath cycle while audio is playing. 1.6s sin-cycle is fast
 * enough to feel "live" but slow enough not to compete with the
 * audio for attention. Per-bar delay = cycle / barCount, so one full
 * wave traverses the row left-to-right over a single cycle, giving
 * the row a subtle running-wave shimmer rather than 72 bars pulsing
 * in lockstep. Disabled on the compact (ops) variant via the
 * `playing` prop being clamped to false at the call site.
 */
const WAVEFORM_BREATH_CYCLE_MS = 1600;

function Waveform({
  progress,
  onSeek,
  playing,
  height = WAVEFORM_HEIGHT_FULL,
}: {
  progress: number;
  onSeek: (fraction: number) => void;
  playing: boolean;
  height?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const handle = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    onSeek(f);
  };
  const headIndex = Math.round(progress * (WAVEFORM_BARS.length - 1));
  // Compact mode: clamp bar heights to the smaller row height so the
  // shortest bars never escape the row.
  const heightScale = height / WAVEFORM_HEIGHT_FULL;
  const barDelayStepMs = WAVEFORM_BREATH_CYCLE_MS / WAVEFORM_BAR_COUNT;

  return (
    <button
      type="button"
      aria-label="Seek"
      onClick={handle}
      className="group relative block w-full min-w-0 cursor-pointer select-none overflow-hidden rounded-md bg-transparent p-0"
      style={{ height }}
    >
      <div ref={ref} className="flex w-full items-center gap-[2px]" style={{ height }}>
        {WAVEFORM_BARS.map((bar, i) => {
          const played = i <= headIndex;
          // `flex-1 min-w-[2px]` distributes the bars across the full row
          // width - safe now that the parent grid track is `minmax(0, 1fr)`
          // (the prior collapse trap is gone). `min-w-[2px]` is the floor
          // so a bar never renders sub-pixel.
          const baseStyle: React.CSSProperties = {
            height: Math.max(2, Math.round(bar.height * heightScale)),
            transformOrigin: "center",
          };
          const playStyle: React.CSSProperties | undefined = playing
            ? {
                animation: `waveform-breath ${WAVEFORM_BREATH_CYCLE_MS}ms ease-in-out infinite`,
                animationDelay: `${i * barDelayStepMs}ms`,
                willChange: "transform",
              }
            : undefined;
          return (
            <span
              key={bar.id}
              aria-hidden="true"
              className={cn(
                "block min-w-[2px] flex-1 rounded-full [transition:background-color_180ms_var(--ease-out-expo)]",
                // Played: brand teal at full saturation.
                // Unplayed: deeper teal-900 hue at low opacity so bars stay
                // clearly visible against the audio section's teal-tinted
                // background - `teal-600/30` was the wrong hue family
                // and washed out. Hover bumps to /50 for affordance.
                played ? "bg-teal-600" : "bg-teal-900/30 group-hover:bg-teal-900/50",
              )}
              style={{ ...baseStyle, ...playStyle }}
            />
          );
        })}
      </div>
    </button>
  );
}

export function BriefSurface({
  copy,
  className,
  variant = "full",
  onMore,
  onMoreLabel,
  expand,
  onPlayingChange,
  onEnded,
  pauseToken = 0,
  resolveAudioUrl,
}: BriefSurfaceProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const mountedRef = useRef(true);
  const objectAudioUrlRef = useRef<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const webAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const webAudioTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nativePlaybackCleanupRef = useRef<(() => void) | null>(null);
  const [playing, setPlaying] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string | null>(() =>
    resolveAudioUrl ? null : (copy.audioUrl ?? null),
  );
  const [audioLoading, setAudioLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState<0.75 | 1 | 1.25 | 1.5 | 2>(1);
  const [expanded, setExpanded] = useState(false);
  const labelId = useId();
  const compact = variant !== "full";
  const preview = variant === "preview";
  // Collapsed preview is the glanceable player; everything else is the full
  // read. Five guards spelled this three different ways.
  const fullRead = !preview || expanded;

  const clearWebAudioTimer = useCallback(() => {
    if (webAudioTimerRef.current === null) return;
    clearInterval(webAudioTimerRef.current);
    webAudioTimerRef.current = null;
  }, []);

  const cleanupNativePlaybackAttempt = useCallback(() => {
    const cleanup = nativePlaybackCleanupRef.current;
    nativePlaybackCleanupRef.current = null;
    cleanup?.();
  }, []);

  const stopWebAudio = useCallback(() => {
    clearWebAudioTimer();
    const source = webAudioSourceRef.current;
    webAudioSourceRef.current = null;
    if (!source) return;
    source.onended = null;
    try {
      source.stop();
    } catch {
      // Already stopped.
    }
  }, [clearWebAudioTimer]);

  const ensureAudioContext = useCallback((): AudioContext | null => {
    if (typeof window === "undefined") return null;
    const win = window as Window & { webkitAudioContext?: typeof AudioContext };
    const AudioContextCtor = window.AudioContext ?? win.webkitAudioContext;
    if (!AudioContextCtor) return null;
    audioContextRef.current ??= new AudioContextCtor();
    return audioContextRef.current;
  }, []);

  const primeWebAudio = useCallback(() => {
    const ctx = ensureAudioContext();
    if (!ctx) return;
    void ctx.resume().catch(() => undefined);
  }, [ensureAudioContext]);

  const playWithWebAudio = useCallback(
    async (src: string): Promise<boolean> => {
      if (!src.startsWith("blob:")) return false;
      const ctx = ensureAudioContext();
      if (!ctx) return false;
      await ctx.resume();
      const res = await fetch(src);
      if (!res.ok) return false;
      const bytes = await res.arrayBuffer();
      const buffer = await ctx.decodeAudioData(bytes.slice(0));
      stopWebAudio();
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute("src");
        el.load();
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.playbackRate.value = rate;
      source.connect(ctx.destination);
      webAudioSourceRef.current = source;
      setDuration(buffer.duration);
      setElapsed(0);
      setPlaying(true);
      const startedAt = ctx.currentTime;
      webAudioTimerRef.current = setInterval(() => {
        setElapsed(Math.min(buffer.duration, ctx.currentTime - startedAt));
      }, 250);
      source.onended = () => {
        if (webAudioSourceRef.current !== source) return;
        webAudioSourceRef.current = null;
        clearWebAudioTimer();
        setElapsed(buffer.duration);
        setPlaying(false);
        onEndedRef.current?.();
      };
      source.start(0);
      return true;
    },
    [clearWebAudioTimer, ensureAudioContext, rate, stopWebAudio],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupNativePlaybackAttempt();
      stopWebAudio();
      void audioContextRef.current?.close().catch(() => undefined);
      if (objectAudioUrlRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(objectAudioUrlRef.current);
      }
    };
  }, [cleanupNativePlaybackAttempt, stopWebAudio]);

  useEffect(() => {
    cleanupNativePlaybackAttempt();
    if (objectAudioUrlRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(objectAudioUrlRef.current);
    }
    objectAudioUrlRef.current = null;
    stopWebAudio();
    setAudioSrc(resolveAudioUrl ? null : (copy.audioUrl ?? null));
    setAudioLoading(false);
    setPlaying(false);
    setElapsed(0);
    setDuration(0);
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.removeAttribute("src");
      el.load();
    }
  }, [cleanupNativePlaybackAttempt, copy.audioUrl, resolveAudioUrl, stopWebAudio]);

  useEffect(() => {
    onPlayingChange?.(playing);
  }, [playing, onPlayingChange]);

  // Another primary audio claimed focus; stop competing with it.
  const pauseTokenRef = useRef(pauseToken);
  useEffect(() => {
    if (pauseToken === pauseTokenRef.current) return;
    pauseTokenRef.current = pauseToken;
    const el = audioRef.current;
    cleanupNativePlaybackAttempt();
    stopWebAudio();
    if (el && !el.paused) el.pause();
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    setPlaying(false);
  }, [cleanupNativePlaybackAttempt, pauseToken, stopWebAudio]);

  // Density tokens. Compact drops the host puck, tightens every padded
  // region, slims the audio transport, clamps the body to 2 lines, and
  // swaps the palette to ops (navy + leaf-dark + mist-dark) so the card
  // sits naturally next to a stat hero on the operations dashboard.
  const headerPad = preview ? "px-6 pt-5 pb-4" : compact ? "px-6 pt-5 pb-3" : "px-7 pt-6 pb-5";
  const bodyPad = preview ? "px-6 pb-5 pt-0" : compact ? "px-6 py-4" : "px-7 py-6";
  const factsPad = compact ? "px-6 py-4" : "px-7 py-5";
  const audioPad = preview ? "px-6 pb-5 pt-4" : compact ? "px-6 py-4" : "px-6 py-4";
  const footerPad = compact ? "px-6 py-3" : "px-7 py-4";

  const eyebrowInkClass = compact ? "text-ink-accent-leaf" : "text-ink-brand";
  const eyebrowDotClass = compact ? "bg-leaf-dark" : "bg-teal-600";
  const headlineInkClass = compact ? "text-ink-primary" : "text-teal-900";
  const bodyInkClass = compact ? "text-ink-body" : "text-teal-900/90";
  const metaInkClass = compact ? "text-ink-tertiary" : "text-teal-900/65";
  // The sources footer was rewritten to a tiny mono uppercase eyebrow that
  // shares its tone tokens (gray-500 / gray-700) with EnvironmentCard's
  // footer, so the prior compact-vs-default `sourcesInkClass` /
  // `sourcesLabelInkClass` ink-class branches are no longer referenced.
  const sectionDividerClass = compact ? "border-t border-border-strong/50" : SECTION_DIVIDER;
  const audioBgClass = preview
    ? "bg-transparent"
    : compact
      ? "bg-surface-accent-leaf/40"
      : "bg-teal-900/[0.03]";
  const showMoreInkClass = compact
    ? "text-ink-accent-leaf hover:text-ink-primary"
    : "text-ink-brand hover:text-teal-900";

  const headlineClass = compact
    ? cn(
        "mt-2 max-w-[34ch] font-title text-[24px] font-medium leading-[1.12] tracking-[-0.02em]",
        headlineInkClass,
      )
    : cn(
        "mt-3 max-w-[34ch] font-title text-[28px] font-medium leading-[1.1] tracking-[-0.02em]",
        headlineInkClass,
      );
  // Body width: 68ch was leaving a lot of dead space on wide viewports
  // because the brief renders inside a full-width grid cell, not in the
  // centre column. Bumping to 92ch lets a typical paragraph use the
  // available width without crossing the readable-line ceiling (~95ch
  // is the upper bound recommended by digital typography research).
  const bodyTextClass = compact
    ? cn("max-w-[92ch] text-base leading-[1.6] tracking-[-0.005em] [&_p]:m-0", bodyInkClass)
    : cn("max-w-[92ch] text-lg leading-[1.55] tracking-[-0.005em] [&_p]:m-0", bodyInkClass);
  const bodyClampClass = preview ? "line-clamp-3" : compact ? "line-clamp-2" : "line-clamp-3";

  // Compact swaps the brief's teal radial gradient shell for the ops
  // dashboard's white-to-leaf-soft gradient + mist-dark border + soft
  // shadow. h-full + flex-col lets the body region grow to fill the
  // grid row height so the card matches the Hero's height when the
  // grid stretches both items to the row max.
  const compactArticleClass = cn(
    "relative flex flex-col overflow-hidden rounded-2xl border border-border-card bg-surface-elevated bg-gradient-card shadow-card",
    "h-full",
  );

  const hasAudio = Boolean(copy.audioUrl);
  // Body content the TTS path could speak. An empty brief (the
  // placeholder shown before there's enough signal) has body=[] -
  // we don't want a play button promising audio that doesn't exist.
  // Validated in user research: Maria called the empty player "a
  // tease, like a podcast cover with no episode" - so the transport
  // disappears entirely when there's nothing to play OR speak.
  // Speakable means there's actual body content - not just a headline.
  // The placeholder brief carries a headline ("Maria, no brief yet today")
  // but body=[]; we don't want a play button pretending to read a brief
  // when there's nothing more than the title to read.
  const hasSpeakableBody = bodyToPlainText(copy.body).trim().length > 0;
  const canSpeak = !hasAudio && SUPPORTS_TTS && hasSpeakableBody;

  // TTS sentence chunks. With no real audioUrl the skip chips have no
  // timeline to seek across, so we approximate +/- 15s with sentence-
  // level navigation: split the body into sentences, advance the chunk
  // index, cancel-and-resume the utterance from the new index.
  const ttsChunksRef = useRef<string[]>([]);
  const ttsIdxRef = useRef(0);

  useEffect(() => {
    const text = bodyToPlainText(copy.body) || copy.headline;
    ttsChunksRef.current = text
      .split(/(?<=[.!?])\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    ttsIdxRef.current = 0;
  }, [copy.body, copy.headline]);

  useEffect(() => {
    return () => {
      if (canSpeak) {
        try {
          window.speechSynthesis.cancel();
        } catch {
          /* noop */
        }
      }
    };
  }, [canSpeak]);

  useEffect(() => {
    const el = audioRef.current;
    if (el) el.playbackRate = rate;
    if (webAudioSourceRef.current) webAudioSourceRef.current.playbackRate.value = rate;
  }, [rate]);

  const playTtsFrom = (idx: number) => {
    const chunks = ttsChunksRef.current;
    if (chunks.length === 0) return;
    const clamped = Math.max(0, Math.min(chunks.length - 1, idx));
    ttsIdxRef.current = clamped;
    const text = chunks.slice(clamped).join(" ");
    if (!text) {
      window.speechSynthesis.cancel();
      setPlaying(false);
      return;
    }
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.onend = () => {
      ttsIdxRef.current = 0;
      setPlaying(false);
    };
    u.onerror = () => setPlaying(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    setPlaying(true);
  };

  const toggle = async () => {
    if (hasAudio && audioRef.current) {
      const el = audioRef.current;
      if (!playing && el.paused && !webAudioSourceRef.current) {
        cleanupNativePlaybackAttempt();
        primeWebAudio();
        let nextSrc = audioSrc;
        if (!nextSrc && copy.audioUrl) {
          setAudioLoading(true);
          try {
            nextSrc = resolveAudioUrl ? await resolveAudioUrl(copy.audioUrl) : copy.audioUrl;
            if (!mountedRef.current) {
              if (nextSrc.startsWith("blob:")) URL.revokeObjectURL(nextSrc);
              return;
            }
            if (objectAudioUrlRef.current?.startsWith("blob:")) {
              URL.revokeObjectURL(objectAudioUrlRef.current);
            }
            objectAudioUrlRef.current = nextSrc.startsWith("blob:") ? nextSrc : null;
            setAudioSrc(nextSrc);
          } catch {
            return;
          } finally {
            if (mountedRef.current) setAudioLoading(false);
          }
        }
        if (!nextSrc) return;
        if (el.src !== nextSrc) {
          el.src = nextSrc;
          el.load();
        }

        let fallbackTimer: number | null = null;
        let fallbackStarted = false;

        function clearFallbackTimer() {
          if (fallbackTimer === null) return;
          window.clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }

        function stopNativeElement() {
          el.pause();
          el.removeAttribute("src");
          el.load();
        }

        function handleNativePlaybackStarted() {
          if (fallbackStarted || webAudioSourceRef.current) {
            stopNativeElement();
            return;
          }
          clearFallbackTimer();
        }

        function cleanupNativeAttempt() {
          clearFallbackTimer();
          el.removeEventListener("playing", handleNativePlaybackStarted);
          el.removeEventListener("timeupdate", handleNativePlaybackStarted);
          if (nativePlaybackCleanupRef.current === cleanupNativeAttempt) {
            nativePlaybackCleanupRef.current = null;
          }
        }

        nativePlaybackCleanupRef.current = cleanupNativeAttempt;
        el.addEventListener("playing", handleNativePlaybackStarted);
        el.addEventListener("timeupdate", handleNativePlaybackStarted);

        const fallbackToWebAudio = async () => {
          if (!mountedRef.current || fallbackStarted || !nextSrc?.startsWith("blob:")) {
            return false;
          }
          if (!el.paused && !el.ended) return false;
          fallbackStarted = true;
          clearFallbackTimer();
          const played = await playWithWebAudio(nextSrc);
          if (!played) fallbackStarted = false;
          return played;
        };
        fallbackTimer = window.setTimeout(() => {
          void fallbackToWebAudio().catch(() => {
            cleanupNativeAttempt();
            setAudioLoading(false);
            setPlaying(false);
          });
        }, 800);
        void el
          .play()
          .then(() => {
            if (fallbackStarted || webAudioSourceRef.current) {
              stopNativeElement();
            }
            cleanupNativeAttempt();
          })
          .catch(() => {
            const fallbackWasAlreadyStarted = fallbackStarted;
            cleanupNativeAttempt();
            if (fallbackWasAlreadyStarted) return;
            void fallbackToWebAudio()
              .then((played) => {
                if (!played) setPlaying(false);
              })
              .catch(() => setPlaying(false));
          });
        setPlaying(true);
      } else {
        cleanupNativePlaybackAttempt();
        stopWebAudio();
        el.pause();
        setPlaying(false);
      }
      return;
    }
    if (!canSpeak) return;
    if (playing) {
      window.speechSynthesis.cancel();
      setPlaying(false);
      return;
    }
    playTtsFrom(ttsIdxRef.current);
  };

  const seek = (fraction: number) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration)) return;
    el.currentTime = fraction * el.duration;
    setElapsed(el.currentTime);
  };

  const skip = (deltaSeconds: number) => {
    if (hasAudio) {
      const el = audioRef.current;
      if (!el || !Number.isFinite(el.duration)) return;
      el.currentTime = Math.max(0, Math.min(el.duration, el.currentTime + deltaSeconds));
      setElapsed(el.currentTime);
      return;
    }
    if (!canSpeak) return;
    // TTS path: each chip moves one sentence regardless of magnitude.
    const next = ttsIdxRef.current + (deltaSeconds > 0 ? 1 : -1);
    playTtsFrom(next);
  };

  const cycleRate = () => {
    setRate((r) => (r === 1 ? 1.25 : r === 1.25 ? 1.5 : r === 1.5 ? 2 : r === 2 ? 0.75 : 1));
  };

  const progress = duration > 0 ? elapsed / duration : 0;
  // JIT narration should not preload just to discover a duration. Show the
  // transport when the server provides an audio URL; metadata fills in after play.
  const playable = hasAudio;
  const durationLabel = formatDurationLabel(duration) ?? copy.durationLabel ?? null;
  const transportTimeLabel =
    preview && duration <= 0
      ? durationLabel
        ? `${durationLabel} listen`
        : "Ready to listen"
      : `${formatTime(elapsed)} / ${formatTime(duration)}`;
  const audioPlayButton = (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={audioLoading}
      aria-pressed={playing}
      aria-label={audioLoading ? "Loading brief audio" : playing ? "Pause brief" : "Play brief"}
      className={cn(
        "relative flex shrink-0 items-center justify-center rounded-full bg-teal-600 text-ink-on-brand transition-transform duration-[120ms] hover:scale-[1.04] active:scale-[0.97]",
        preview
          ? TRANSPORT_BTN_SIZE_PREVIEW
          : compact
            ? TRANSPORT_BTN_SIZE_COMPACT
            : TRANSPORT_BTN_SIZE_FULL,
        audioLoading && "cursor-wait opacity-80",
      )}
      style={{ boxShadow: PLAY_BTN_SHADOW }}
    >
      {audioLoading ? (
        <span
          aria-hidden="true"
          className="h-5 w-5 animate-spin rounded-full border-2 border-surface-elevated/30 border-t-white"
        />
      ) : (
        <>
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-0 grid place-items-center transition-opacity duration-[140ms] ease-[var(--ease-out-expo)]",
              playing ? "opacity-0" : "opacity-100",
            )}
          >
            <Play size={preview ? 22 : compact ? 20 : 26} weight="fill" />
          </span>
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute inset-0 grid place-items-center transition-opacity duration-[140ms] ease-[var(--ease-out-expo)]",
              playing ? "opacity-100" : "opacity-0",
            )}
          >
            <Pause size={preview ? 22 : compact ? 20 : 26} weight="fill" />
          </span>
        </>
      )}
    </button>
  );

  const audioRateChip = hasAudio ? (
    <button
      type="button"
      onClick={cycleRate}
      aria-label={`Playback speed ${rate}x`}
      className={cn(
        "shrink-0 rounded-full bg-surface-elevated/70 px-2 py-0.5 text-xs font-semibold tabular-nums transition-colors hover:bg-surface-elevated",
        compact
          ? "border border-border-strong/50 text-ink-primary/80"
          : "border border-teal-900/10 text-teal-900/80",
      )}
    >
      {rate}×
    </button>
  ) : null;

  const highlightInkClass = compact ? "text-ink-accent-leaf" : "text-ink-brand";
  const emphasisInkClass = compact ? "text-ink-primary" : "text-teal-900";
  // Body segments are immutable per render (the brief is regenerated as a
  // unit; segments don't reorder mid-render), but two segments can share
  // identical text. Combining the segment text with a running offset
  // produces a key that's stable AND unique within the body without
  // depending on array index alone.
  const bodyNode =
    typeof copy.body === "string" ? (
      <p>{copy.body}</p>
    ) : Array.isArray(copy.body) ? (
      <p>
        {(() => {
          let textOffset = 0;
          const segs = copy.body;
          return segs.map((seg, i) => {
            const key = `${textOffset}-${seg.text.slice(0, 16)}`;
            textOffset += seg.text.length;
            const isLast = i === segs.length - 1;
            return (
              <span
                key={key}
                className={cn(
                  seg.highlight && cn("font-semibold", highlightInkClass),
                  seg.emphasis && cn("font-semibold", emphasisInkClass),
                )}
              >
                {seg.text}
                {!isLast ? " " : ""}
              </span>
            );
          });
        })()}
      </p>
    ) : null;

  const host = copy.host;
  // An empty eyebrow is a caller saying the surface around the card already
  // names it, which is different from a caller not setting one at all.
  const eyebrowText = [copy.eyebrow ?? "WEEKLY BRIEF", copy.date].filter(Boolean).join(" · ");
  const sections = copy.sections ?? [];
  const hasSections = sections.length > 0;
  // Total prose length across body segments. The body is clamped to ~3 lines
  // (~80ch each) by `bodyClampClass`, so anything over ~220 chars will be cut
  // off and needs a "Show more" affordance even if there are no facts sections.
  // `copy.body` is `string | BriefBodySegment[]` - bodyToPlainText handles
  // both shapes, then we just measure the resulting string.
  const bodyTextLength = bodyToPlainText(copy.body ?? "").length;
  const hasToggle = hasSections || bodyTextLength > 220;

  return (
    <article
      aria-labelledby={labelId}
      className={cn(
        compact ? compactArticleClass : CARD_VARIANT_CLASS.brief,
        "@container isolate animate-fade-in-up",
        compact ? "text-ink-primary" : "text-teal-900",
        className,
      )}
      style={compact ? undefined : CARD_VARIANT_STYLE.brief}
    >
      {/*
       * Playback breath overlay. While `playing === true` (full variant
       * only), a soft teal halo sits just above the card's bg-gradient
       * and just below content via z-index: -1 inside the article's
       * isolated stacking context. Drifts on a 16s cycle in sympathy
       * with the per-bar waveform breath, so the WHOLE card feels
       * alive while audio plays. Snaps off when paused - span unmounts.
       */}
      {!compact && playing && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
          style={{ zIndex: -1 }}
        >
          <span
            className="absolute"
            style={{
              top: "-15%",
              left: "20%",
              width: "70%",
              height: "70%",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, color-mix(in srgb, var(--color-teal-300) 12%, transparent), transparent 65%)",
              filter: "blur(40px)",
              animation: "brief-shell-drift 16s ease-in-out infinite alternate",
              willChange: "transform, opacity",
            }}
          />
        </span>
      )}
      {/* ─── HEADER ─────────────────────────────────────────────── */}
      <header className={cn("flex flex-wrap items-start justify-between gap-3", headerPad)}>
        <div className="min-w-0 flex-1">
          {eyebrowText ? (
            preview ? (
              // Paired with another card's eyebrow rather than standing alone,
              // so it takes the same dotless mono the other Today cards use.
              <EyebrowBullet variant="plain" className="mb-1">
                {eyebrowText}
              </EyebrowBullet>
            ) : (
              <p
                className={cn(
                  "inline-flex items-center gap-1.5 text-xs font-medium",
                  eyebrowInkClass,
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn("inline-block h-1.5 w-1.5 rounded-full", eyebrowDotClass)}
                />
                {eyebrowText}
              </p>
            )
          ) : null}
          <h2 id={labelId} className={headlineClass}>
            {copy.headline}
          </h2>
          {/* Compact renders the subtitle as the narrator lede under the
              greeting headline (ops brief anatomy); full keeps the larger
              patient-facing subtitle. */}
          {copy.subtitle &&
            (compact ? (
              <p className="mt-1.5 max-w-[72ch] text-sm leading-relaxed text-ink-secondary">
                {copy.subtitle}
              </p>
            ) : (
              <p className="mt-2 max-w-[64ch] text-base leading-relaxed text-teal-900/70">
                {copy.subtitle}
              </p>
            ))}
        </div>
        <div className="flex shrink-0 items-start gap-2">
          {!preview && (durationLabel || copy.updatedLabel) && (
            <div className={cn("flex flex-col items-end gap-0.5 text-right text-xs", metaInkClass)}>
              {durationLabel && (
                <p className="inline-flex items-center gap-1.5 font-medium">
                  <Headphones size={13} />
                  <span>{durationLabel}</span>
                </p>
              )}
              {copy.updatedLabel && (
                <p className={cn("text-xs", compact ? "text-ink-tertiary" : "text-teal-900/55")}>
                  {copy.updatedLabel}
                </p>
              )}
            </div>
          )}
          {expand && (
            <button
              type="button"
              aria-expanded={expand.expanded}
              aria-label={expand.label}
              title={expand.label}
              // The expand/collapse cue is played by the host on toggle; opt out
              // of the default press so it is not doubled.
              data-silent=""
              onClick={expand.onToggle}
              className={cn(
                "grid h-[26px] w-[26px] place-items-center rounded-sm transition motion-safe:active:scale-95",
                compact
                  ? "text-ink-tertiary hover:bg-surface-track hover:text-ink-body"
                  : "text-teal-900/55 hover:bg-teal-900/5 hover:text-teal-900",
              )}
            >
              {expand.expanded ? (
                <ArrowsInSimple size={15} weight="bold" />
              ) : (
                <ArrowsOutSimple size={15} weight="bold" />
              )}
            </button>
          )}
          {onMore && (
            <button
              type="button"
              aria-label={onMoreLabel ?? "More"}
              title={onMoreLabel ?? "More"}
              onClick={onMore}
              className={cn(
                "grid h-[26px] w-[26px] place-items-center rounded-sm transition",
                compact
                  ? "text-ink-tertiary hover:bg-surface-track hover:text-ink-body"
                  : "text-teal-900/55 hover:bg-teal-900/5 hover:text-teal-900",
              )}
            >
              <DotsThree size={16} weight="bold" />
            </button>
          )}
        </div>
      </header>

      {/* ─── BODY (host + narrative) ─────────────────────────────
          Compact drops the host puck (saves ~140px of vertical mass),
          renders a single-column body, and uses flex-1 so it grows to
          absorb the leftover grid-row height. That makes the card
          match the Hero's stretched height pixel-for-pixel. */}
      {(host || bodyNode) && (
        <section
          className={cn(sectionDividerClass, bodyPad, compact && "flex-1", preview && "border-t-0")}
        >
          <div
            className={cn(
              // Two columns while the narration is clamped, which is the shape a portrait beside a paragraph wants.
              compact && host && expanded ? "block" : "grid grid-cols-1",
              !compact && host && "gap-5 md:grid-cols-[128px_1fr] md:gap-6",
              compact && host && !expanded && "gap-4 md:grid-cols-[auto_minmax(0,1fr)] md:gap-5",
            )}
          >
            {/* Compact host: the portrait alone. The name and the role sized
                this column, so a job title decided how much room the narration
                beside it got, and the full variant states both anyway. The
                brand orb stands in for a host we ship no photo of. */}
            {host && compact && (
              <span
                className={cn(
                  "h-14 w-14 shrink-0 overflow-hidden rounded-xl shadow-card ring-1 ring-surface-elevated/80",
                  expanded && "float-left mr-4 mb-2 block",
                )}
              >
                {host.photoUrl ? (
                  <img
                    src={host.photoUrl}
                    alt={host.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span aria-hidden="true" className="grid h-full w-full place-items-center">
                    <BrandMark size="hero" />
                  </span>
                )}
              </span>
            )}
            {host && !compact && (
              <div className="flex flex-col items-start gap-2">
                <div className="h-[112px] w-[112px] overflow-hidden rounded-xl shadow-card ring-1 ring-surface-elevated/80">
                  {host.photoUrl ? (
                    <img
                      src={host.photoUrl}
                      alt={host.name}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      aria-hidden="true"
                      className="flex h-full w-full items-center justify-center bg-linear-to-br from-teal-500 to-leaf font-sans text-2xl font-bold text-ink-on-media"
                    >
                      {initialsOf(host.name)}
                    </div>
                  )}
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-bold tracking-[-0.005em] text-teal-900">{host.name}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-ink-brand">
                    <span
                      aria-hidden="true"
                      className="inline-block h-1.5 w-1.5 rounded-full bg-teal-600"
                    />
                    {host.tagline ?? "Your briefing host"}
                  </p>
                </div>
              </div>
            )}
            {bodyNode && (
              <div className="min-w-0">
                <div
                  className={cn(bodyTextClass, !expanded && bodyClampClass)}
                  style={{ fontWeight: 450 }}
                >
                  {bodyNode}
                </div>
                {hasToggle && (
                  <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full font-medium transition-colors",
                      showMoreInkClass,
                      compact ? "mt-3 text-xs" : "mt-3 text-xs",
                    )}
                  >
                    {expanded ? "Show less" : "Show more"}
                    <span
                      aria-hidden="true"
                      className={cn(
                        "transition-transform duration-[200ms] ease-[var(--ease-out-expo)]",
                        expanded && "rotate-180",
                      )}
                    >
                      <CaretDown size={13} />
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {hasSections && expanded && (
        <section className={cn(sectionDividerClass, factsPad)}>
          <div
            className={cn(
              "grid grid-cols-1 sm:grid-cols-3 sm:divide-x sm:divide-y-0",
              compact
                ? "divide-y divide-mist-dark/40 sm:divide-mist-dark/40"
                : "divide-y divide-teal-900/10 sm:divide-teal-900/10",
            )}
          >
            {sections.map((s, i) => (
              <div
                key={`${s.overline}-${s.title}`}
                className={cn(
                  "min-w-0",
                  // vertical rhythm: first column has no left padding, others get it
                  // so the divider lines sit flush to the content on either side
                  i === 0 ? "sm:pr-5" : i === sections.length - 1 ? "sm:pl-5" : "sm:px-5",
                  "py-3 first:pt-0 last:pb-0 sm:py-0",
                )}
                style={{
                  animation: `fade-in-content 300ms var(--ease-out-expo) ${i * 70}ms both`,
                }}
              >
                <p
                  className={cn(
                    "text-xs font-medium",
                    compact ? "text-ink-tertiary" : "text-teal-900/55",
                  )}
                >
                  {s.overline}
                </p>
                <p
                  className={cn(
                    "mt-1 text-base font-bold leading-tight tracking-[-0.005em]",
                    headlineInkClass,
                  )}
                >
                  {s.title}
                </p>
                <p
                  className={cn(
                    "mt-1 text-sm leading-relaxed",
                    compact ? "text-ink-secondary" : "text-teal-900/70",
                  )}
                >
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── AUDIO PLAYER ───────────────────────────────────────── */}
      {!playable && !canSpeak && (hasAudio || hasSpeakableBody) && (
        <section className={cn(sectionDividerClass, audioBgClass, audioPad)}>
          <p
            className={cn(
              "m-0 font-mono text-xs tracking-[0.02em]",
              compact ? "text-ink-tertiary" : "text-teal-900/65",
            )}
          >
            Narration is not available for this brief.
          </p>
        </section>
      )}
      {(playable || canSpeak) && !fullRead && (
        // Preview is a two-row glanceable player, not a three-band layout:
        // button + waveform share a row (the waveform reads as an extension
        // of the button, not a separate floating block), and the time/speed
        // row sits directly under it, indented to align under the waveform
        // rather than sprawling the full card width.
        <section className={cn(sectionDividerClass, audioBgClass, audioPad, "flex flex-col gap-2")}>
          <div className="flex items-center gap-3">
            {audioPlayButton}
            <div className="min-w-0 flex-1">
              <Waveform
                progress={progress}
                onSeek={seek}
                height={WAVEFORM_HEIGHT_PREVIEW}
                playing={false}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 pl-[60px]">
            <span className="font-mono text-xs tabular-nums text-teal-900/65">
              {transportTimeLabel}
            </span>
            {audioRateChip}
          </div>
        </section>
      )}
      {(playable || canSpeak) && fullRead && (
        <section
          // Narrow containers stack the row (controls / waveform / time) so the
          // 72-bar waveform always gets a full-width track instead of being
          // crushed below its min-width and spilling into the time label. At
          // >=40rem the three-column layout returns. Tailwind's grid-cols-*
          // use minmax(0,1fr), which is the fix that stops the
          // waveform collapsing to 0 width (bare 1fr = minmax(auto,1fr)).
          className={cn(
            sectionDividerClass,
            // Two rows at panel width:
            "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2",
            "@[40rem]:grid-cols-[auto_minmax(0,1fr)_auto] @[40rem]:gap-x-4 @[40rem]:gap-y-0",
            compact && !host && !bodyNode && "my-auto border-t-0",
            audioBgClass,
            audioPad,
          )}
        >
          {/* Transport: podcast-style -15s / play / +15s controls.
              Compact shows only the forward chip (the back chip is rare
              in practice and adds noise next to a stat hero). An expanded
              preview is a full read, so it gets both. With TTS fallback the
              chips advance one sentence at a time. */}
          <div className={cn("flex items-center", compact ? "gap-1" : "gap-1.5")}>
            {(!compact || fullRead) && (hasAudio || canSpeak) && (
              <SkipChip compact={compact} seconds={-15} onClick={() => skip(-15)} />
            )}
            {audioPlayButton}
            {(hasAudio || canSpeak) && (
              <SkipChip compact={compact} seconds={15} onClick={() => skip(15)} />
            )}
          </div>

          {/* Waveform IS the progress bar. The host attribution lives in the
              host block above the audio row; no need to repeat it here. */}
          <div className="flex min-w-0 flex-col justify-center">
            <Waveform
              progress={progress}
              onSeek={seek}
              height={compact ? WAVEFORM_HEIGHT_COMPACT : WAVEFORM_HEIGHT_FULL}
              playing={!compact && playing}
            />
          </div>

          {/* Elapsed / duration + speed. Under the waveform while the panel is
              narrow, back on the end of the row once there is width for it. */}
          <div
            className={cn(
              "col-start-2 flex items-center justify-between",
              "@[40rem]:col-start-3 @[40rem]:row-start-1 @[40rem]:shrink-0 @[40rem]:justify-start",
              compact ? "gap-2" : "gap-3",
            )}
          >
            <span
              className={cn(
                "font-mono text-xs tabular-nums",
                compact ? "text-ink-secondary" : "text-teal-900/65",
              )}
            >
              {transportTimeLabel}
            </span>
            {audioRateChip}
          </div>
        </section>
      )}

      {/* ─── FOOTER (sources) - matches the EnvironmentCard footer pattern:
              tiny uppercase mono tracking, mist-bordered top rule. Reads as
              an editorial attribution chip rather than a chunky body line. */}
      {/* Provenance belongs with the full read, not under the player: on the
          preview it pushed the transport up the panel and left the list beside
          it with dead space under its last row. */}
      {copy.sources?.length && fullRead ? (
        <footer
          className={cn(
            "flex flex-wrap items-baseline gap-y-1 border-t border-border-default pt-3 font-sans font-medium text-xs text-ink-tertiary",
            footerPad,
          )}
        >
          {/* The separator belongs to the item before it. Leading each source
              with its own bullet meant a wrap put the bullet at the start of
              the new line, orphaned from the item it was dividing. Each source
              also stays on one line, so a wrap falls between sources rather
              than through the middle of one. */}
          <span className="whitespace-nowrap font-semibold text-ink-body">
            Sources
            <span aria-hidden="true" className="px-2 font-normal text-ink-quaternary">
              ·
            </span>
          </span>
          {copy.sources.map((src, i) => (
            <span key={src} className="whitespace-nowrap">
              {src}
              {i < (copy.sources?.length ?? 0) - 1 ? (
                <span aria-hidden="true" className="px-2 text-ink-quaternary">
                  ·
                </span>
              ) : null}
            </span>
          ))}
        </footer>
      ) : null}

      {/* Hidden audio element drives the player. preload=none keeps JIT audio
       * generation tied to an explicit play action. */}
      {hasAudio && (
        // biome-ignore lint/a11y/useMediaCaption: caption track activates when TAM ships transcript URLs alongside audioUrl
        <audio
          ref={audioRef}
          src={audioSrc ?? undefined}
          preload="none"
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onDurationChange={(e) => setDuration(e.currentTarget.duration || 0)}
          onTimeUpdate={(e) => setElapsed(e.currentTarget.currentTime || 0)}
          onEnded={() => {
            setPlaying(false);
            onEndedRef.current?.();
          }}
          onPause={() => {
            if (!webAudioSourceRef.current) setPlaying(false);
          }}
          onPlay={() => setPlaying(true)}
          onError={() => {
            setAudioLoading(false);
            setPlaying(false);
          }}
        />
      )}
    </article>
  );
}
